import * as Yup from "yup";
import { timeDiffInHours } from "src/utils/utility";
import DOMPurify from "dompurify";

export const noEmojis = (value) => {
  if (typeof value !== "string") return true;

  const emojiRegex =
    /([\u2700-\u27BF]|[\uE000-\uF8FF]|[\uD83C-\uDBFF\uDC00-\uDFFF]|[\u2011-\u26FF])/g;
  return !emojiRegex.test(value);
};

export const sanitizeString = (value) => {
  if (typeof value === "string") {
    // Sanitize the string
    const sanitizedValue = DOMPurify.sanitize(value, {
      ALLOWED_TAGS: [],
    }).trim();
    return sanitizedValue; // Return the sanitized value
  }
  return value; // Return the original value if not a string
};

export const sanitizationTest = function (value, context) {
  const original = context.originalValue;
  const sanitized = sanitizeString(original);
  if (original !== sanitized) {
    return context.createError({
      message: "Input contained invalid characters",
    });
  }
  return true;
};

export const RegisterSchema = Yup.object().shape({
  firstName: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("First name required"),
  lastName: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Last name required"),
  email: Yup.string()
    .email("Email must be a valid email address")
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Email is required"),
  phone: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Email is required"),
  companyName: Yup.string()
    .email("Email must be a valid email address")
    .required("Email is required"),
  GSTIN: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis),
  companyAddress: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Address is required"),
  pincode: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Pincode is required"),
  city: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("City is required"),
  state: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("State is required"),
  country: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Country is required"),
});

export const chargerFormValidationSchema = Yup.object().shape({
  chargepoint_id: Yup.string()
    .transform(sanitizeString)
    .max(60, "Must be 20 characters or less")
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Chargerbox Id is required")
    .matches(/^[a-zA-Z0-9_]+$/, "No special characters allowed"),
  chargepoint_name: Yup.string()
    .transform(sanitizeString)
    .max(60, "Must be 50 characters or less")
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Chargerbox name is required"),
  street: Yup.string()
    .max(1000, "Must be 1000 characters or less")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Address Line 1 is required"),
  houseNumber: Yup.string()
    .max(100, "Must be 100 characters or less")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis),
  zipCode: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("PIN Code is required"),
  registrationStatus: Yup.string()
    .required("Please select registration status")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis),
  city: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .max(60, "Must be 60 characters or less")
    .required("City is required"),
  country: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Country is required"),
  state: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .max(60, "Must be 60 characters or less")
    .nullable(!0),
  noOfConnectors: Yup.number().typeError("Only digits is allowed"),
  chargerVisibility: Yup.boolean(),
  employeeOrResident: Yup.boolean(),
  payWithWallet: Yup.boolean(),
  payWithCard: Yup.boolean(),
  tolerance_watt: Yup.number().required(
    "Please define a tolerance level (in watts)"
  ),
  meter_interval_sec: Yup.number().required(
    "Please define a meter interval (in seconds)"
  ),
  operator_email: Yup.string()
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .email("Invalid email"),
});

export const transactionStatusValidationSchema = Yup.object().shape({
  notes: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .max(600, "Must be 600 characters or less"),
  status: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis),
});

export const walletAdjustmentValidationSchema = Yup.object().shape({
  adjustmentType: Yup.string()
    .oneOf(["DEBIT", "CREDIT"], "Please choose Debit or Credit")
    .required("Please choose Debit or Credit"),
  amount: Yup.number()
    .typeError("Amount must be greater than 0")
    .moreThan(0, "Amount must be greater than 0")
    .required("Amount is required"),
  remarks: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Remarks is required"),
  ticketId: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Ticket ID is required"),
  doneByName: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Done By is required"),
});

const onlyText = (value) => {
  if (!value) return true;
  return /^[A-Za-z\s]+$/.test(value);
};
const onlyDigits = (value) => {
  if (!value) return true;
  return /^[0-9]+$/.test(value);
};
const sanitizeText = (value) =>
  value ? value.replace(/[^A-Za-z\s]/g, "") : value;
const sanitizeMobile = (value) =>
  value ? value.replace(/[^0-9]/g, "") : value;

export const payoutAccountValidationSchema = Yup.object().shape({
  business_name: Yup.string()
    .transform(sanitizeText)
    .test("only-text", "Only alphabets and spaces allowed", onlyText)
    .max(50, "Must be 50 characters or less"),
  beneficiary_name: Yup.string()
    .transform(sanitizeText)
    .test("only-text", "Only alphabets and spaces allowed", onlyText)
    .max(50, "Must be 50 characters or less"),
  phone: Yup.string()
    .transform(sanitizeMobile)
    .test("only-digits", "Only numbers allowed", onlyDigits)
    .min(8, "Must be at least 8 digits")
    .max(15, "Must be 15 digits or less"),
  business_type: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis),
  email: Yup.string()
    .email("Invalid email")
    .test("no-emojis", "Emojis are not allowed", noEmojis),
  account_type: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis),
  payout_percentage: Yup.number()
    .typeError("Only digits are allowed")
    .nullable()
    .notRequired(),
  tax_percentage: Yup.number()
    .typeError("Only digits are allowed")
    .nullable()
    .notRequired(),
});

export const financeConfigValidationSchema = {
  charger_payment_details: Yup.object().shape({
    pricing_model: Yup.string().oneOf(['M', 'Y', 'O']),
    dc_charges_monthly: Yup.number().typeError("Only digits are allowed"),
    ac_charges_monthly: Yup.number().typeError("Only digits are allowed"),
    dc_charges_annual: Yup.number().typeError("Only digits are allowed"),
    ac_charges_annual: Yup.number().typeError("Only digits are allowed"),
    dc_charges_one_time: Yup.number().typeError("Only digits are allowed"),
    ac_charges_one_time: Yup.number().typeError("Only digits are allowed"),
    tax: Yup.number().typeError("Only digits are allowed"),
    other_fixed_cost: Yup.number().typeError("Only digits are allowed"),
    trial_period: Yup.number().integer("Must be a whole number").min(0, "Must be 0 or more").typeError("Only digits are allowed"),
  }),
  transaction_payment_details: Yup.object().shape({
    pg_charges_bearer: Yup.string().oneOf(["CPO", "EV"], "Invalid selection"),
    payment_gateway_percent: Yup.number().typeError("Only digits are allowed"),
    tax: Yup.number().typeError("Only digits are allowed"),
  }),
  payout_details: Yup.object().shape({
    payout_rule: Yup.string()
      .transform(sanitizeString)
      .test("sanitized", "Input contained invalid characters", sanitizationTest)
      .test("no-emojis", "Emojis are not allowed", noEmojis),
    tax_bearer: Yup.string().oneOf(["CPO", "ZYNKA"], "Invalid selection"),
    tax: Yup.number().typeError("Only digits are allowed"),
    chargemiles_commission_in_percent: Yup.boolean(),
    chargemiles_commission_percent: Yup.number().typeError(
      "Only digits are allowed"
    ),
    chargemiles_commission_cost: Yup.number().typeError(
      "Only digits are allowed"
    ),
  }),
};

export const teamFormValidationSchema = Yup.object().shape({
  name: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .max(50, "Must be 50 characters or less")
    .required("Team name is required"),
  operatorName: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .max(50, "Must be 50 characters or less")
    .required("Operator name is required"),
  active: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("status is required"),
  street: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Address Line 1 is required"),
  zipCode: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("PIN Code is required"),
  city: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("City is required"),
  country: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Country is required"),
  // .required("Account number is required")
  payoutAccounts: Yup.array().of(payoutAccountValidationSchema),
  financeConfig: Yup.object().shape(financeConfigValidationSchema),
  settlement_percentage: Yup.number(),
  billingInfo: Yup.object().shape({
    hasGst: Yup.boolean(),
    pan: Yup.string()
      .transform(sanitizeString)
      .uppercase()
      .required("PAN is required")
      .matches(
        /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
        "Enter a valid PAN (e.g. ABCDE1234F)"
      ),
    gstin: Yup.string()
      .transform(sanitizeString)
      .uppercase()
      .when("hasGst", {
        is: true,
        then: (schema) =>
          schema
            .required("GSTIN is required")
            .matches(
              /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
              "Enter a valid 15-character GSTIN"
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
  }),
});

export const companyFormValidationSchema = Yup.object().shape({
  name: Yup.string()
    .max(60, "Must be 20 characters or less")
    .required("Company Name Id is required")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis),
  active: Yup.string()
    .max(60, "Must be 20 characters or less")
    .required("Feature Id is required")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis),
  rootUser: Yup.string()
    .max(60, "Must be 20 characters or less")
    .required("Root user Id is required")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis),
  firstName: Yup.string()
    .required("Root user first name is required")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis),
  lastName: Yup.string()
    .required("Root user last name is required")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis),
  emailId: Yup.string()
    .max(60, "Must be 20 characters or less")
    .required("Email Id is required")
    .test("no-emojis", "Emojis are not allowed", noEmojis),
  paymentCurrency: Yup.string().required("Please select a currency"),
  // transactionPercent:Yup.string()
  // .required("Please specify transaction percentage")
  // .test("is-float","Specify valid decimal value of upto 2 places",(val)=>/^\d+(\.\d{1,2})?$/.test(val)),
  // taxPercent:Yup.string()
  // .required("Please specify tax percentage")
  // .test("is-float","Specify valid decimal value of upto 2 places",(val)=>/^\d+(\.\d{1,2})?$/.test(val)),
  // comments: Yup.string()
  //   .max(60, "Must be 20 characters or less")
  //   .required("Comments is required"),
  // address: Yup.object().shape({
  //   street: Yup.string()
  //     .max(20, "Must be 60 characters or less")
  //     .required("Street is required"),
  //   zipCode: Yup.number()
  //     .typeError("Please specify only numbers")
  //     .required("Zipcode is required"),
  //   houseNumber: Yup.number()
  //     .typeError("Please specify only numbers")
  //     .required("HouseNUmber is required"),
  //   city: Yup.string()
  //     .max(60, "Must be 60 characters or less")
  //     .required("City is required"),
  //   country: Yup.string().required("Please select country"),
  // }),
});

export const companyinfoValidationSchema = Yup.object().shape({
  name: Yup.string()
    .max(60, "Must be 20 characters or less")
    .required("Company Name Id is required")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis),
  active: Yup.string()
    .max(60, "Must be 20 characters or less")
    .required("Feature Id is required")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis),
  rootUser: Yup.string()
    .max(60, "Must be 20 characters or less")
    .required("Root user Id is required")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis),
  firstName: Yup.string()
    .required("Root user first name is required")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis),
  lastName: Yup.string()
    .required("Root user last name is required")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis),
  emailId: Yup.string()
    .max(60, "Must be 20 characters or less")
    .required("Email Id is required")
    .test("no-emojis", "Emojis are not allowed", noEmojis),
  paymentCurrency: Yup.string().required("Please select a currency"),
  charger_configuration_url: Yup.string()
    .nullable()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .matches(/\/$/, "URL must end with '/'"),
});

export const whitelabelInfoValidationSchema = Yup.object().shape({
  whitelabelInfo: Yup.object().shape({
    webapp_url: Yup.string()
      .required("Web App URL is required")
      .matches(
        /^https?:\/\/[^\s/$.?#].[^\s]*\/charger\/(.*)?$/i,
        "URL must start with http/https and include '/charger/'"
      ),
  }),
});

export const taxBillingConfigValidationSchema = Yup.object().shape({
  taxBillingConfig: Yup.object().shape({
    taxLabel: Yup.string()
      .transform(sanitizeString)
      .test("sanitized", "Input contained invalid characters", sanitizationTest)
      .test("no-emojis", "Emojis are not allowed", noEmojis),
    taxIDValue: Yup.string()
      .transform(sanitizeString)
      .test("sanitized", "Input contained invalid characters", sanitizationTest)
      .test("no-emojis", "Emojis are not allowed", noEmojis)
      .max(20, "Must be 20 characters or less"),
    invoiceNumbering: Yup.object().shape({
      invoicePrefix: Yup.string()
        .transform(sanitizeString)
        .test("sanitized", "Input contained invalid characters", sanitizationTest)
        .test("no-emojis", "Emojis are not allowed", noEmojis)
        .matches(/^[A-Za-z0-9]*$/, "Only letters and numbers are allowed")
        .max(10, "Must be 10 characters or less"),
      sequenceLength: Yup.number()
        .typeError("Must be a number")
        .integer("Must be a whole number")
        .min(1, "Must be at least 1")
        .max(12, "Must be 12 or less"),
      rangeStart: Yup.number()
        .typeError("Must be a number")
        .integer("Must be a whole number")
        .min(0, "Must be 0 or more")
        .nullable(),
      rangeEnd: Yup.number()
        .typeError("Must be a number")
        .integer("Must be a whole number")
        .min(0, "Must be 0 or more")
        .nullable()
        .test(
          "range-valid",
          "Range End must be greater than Range Start",
          function (value) {
            const { rangeStart } = this.parent;
            if (value === undefined || value === null || value === "") return true;
            if (rangeStart === undefined || rangeStart === null || rangeStart === "") return true;
            return Number(value) > Number(rangeStart);
          }
        ),
    }),
  })
});

export const thirdPartyValidationSchema = Yup.object().shape({
  public_key: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis),
  secret_key: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis),
  merchant_id: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis),
  webhook_key: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis),
});

export const communicationValidationSchema = Yup.object().shape({
  communication: Yup.object().shape({
    feedback_email: Yup.string().test(
      "is-comma-separated-emails",
      "Please enter valid comma separated email addresses",
      (value) => {
        if (!value || value.trim() === "") return true;
        return value
          .split(",")
          .map((v) => v.trim())
          .every(
            (email) => !!email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
          );
      }
    ),
    support_email: Yup.string().test(
      "is-comma-separated-emails",
      "Please enter valid comma separated email addresses",
      (value) => {
        if (!value || value.trim() === "") return true;
        return value
          .split(",")
          .map((v) => v.trim())
          .every(
            (email) => !!email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
          );
      }
    ),
  }),
});

export const tagFormValidationSchema = Yup.object().shape({
  tag: Yup.string()
    .max(20, "Must be 20 characters or less")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("ID Tag is required"),
  balance_amount: Yup.number()
    .typeError("Balance amount must be a number")
    .moreThan(1, "Balance amount cannot be zero. Please topup")
    .nullable(),
  expirydate: Yup.date()
    .typeError("Please enter a valid date")
    .min(new Date(), "Please provide a valid future date.")
    .required("Expiry Date is required"),
});

export const priceGroupValidationSchema = Yup.object().shape(
  {
    name: Yup.string()
      .max(35, "Must be 35 characters or less")
      .transform(sanitizeString)
      .test("sanitized", "Input contained invalid characters", sanitizationTest)
      .test("no-emojis", "Emojis are not allowed", noEmojis)
      .required("Price Group name is required"),
    priceDetails: Yup.array().of(
      Yup.object().shape({
        priceperMin: Yup.number().when("priceType", {
          is: "min",
          then: Yup.number().required("Price Per Min is required"),
          otherwise: Yup.number().default(1),
        }),
        priceperKW: Yup.number().when("priceType", {
          is: "kwh",
          then: Yup.number()
            .positive("Price Per kwh must be a positive number")
            .moreThan(0, "Price Per kwh must be greater than 0")
            .required("Price Per kwh is required"),
          otherwise: Yup.number().default(1),
        }),
        fixedFeeStarting: Yup.number().min(
          0,
          "Must be greater than or equal to 0"
        ),
        fixedFeeCharging: Yup.number().min(
          0,
          "Must be greater than or equal to 0"
        ),
        startTime: Yup.string().required("Start Time is required"),
        endTime: Yup.string()
          .required("End Time is required")
          .test(
            "endTime",
            "End Time must be greater than Start Time",
            function () {
              return (
                timeDiffInHours(this.parent.startTime, this.parent.endTime) > 0
              );
            }
          ),
      })
    ),
  },
  [["startTime", "endTime"]]
);

export const userFormValidationSchema = Yup.object().shape({
  username: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Username is required"),
  firstName: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("First Name is required"),
  lastName: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Last Name is required"),
  email: Yup.string()
    .email("Invalid Email")
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Email is required"),
  phone: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .matches(/^\+?[0-9]*$/, "Only numbers are allowed, + can only be at the beginning")
    .max(15, "Must be 15 digits or less"),
  canAccessParent: Yup.boolean(),
 // roleId: Yup.string().required("Role is required"),
});

export const loginFormValidationSchema = Yup.object().shape({
  domainName: Yup.string().test(
    "no-emojis",
    "Emojis are not allowed",
    noEmojis
  ),
  username: Yup.string()
    .required("Username is required")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis),
  password: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Password is required"),
});

export const resetPasswordValidationSchmea = Yup.object().shape({
  username: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Username is required"),
});

export const verficationValidationSchema = Yup.object().shape({
  otp: Yup.number()
    .typeError("Numbers is allowed")
    .required("Please input otp"),
  old_password: Yup.string()
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Please input old password"),
  new_password: Yup.string()
    .required("Please input new password")
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .matches(
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{10,}$/,
      "Must Contain 10 Characters, One Uppercase, One Lowercase, One Number and one special case Character"
    ),
  confirm_password: Yup.string()
    .required("Please input confirm password")
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .oneOf([Yup.ref("new_password"), null], `Passwords doesn't match`),
});

export const roleFormValidationSchema = Yup.object().shape({
  roleName: Yup.string()
    .max(50, "Must be 50 characters or less")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Role name is required"),
});

export const customerValidationSchema = Yup.object().shape({
  firstName: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("First Name is required"),
  lastName: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Last Name is required"),
  email: Yup.string()
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .email("Invalid email"),
  phone: Yup.string().matches(/^[0-9]{10}$/, "Phone number must not exceed 10 digits."),
  street: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Address Line 1 is required"),
  zipCode: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("PIN Code is required"),
  city: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("City is required"),
  country: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Country is required"),
  // ocppTagId: Yup.string().required("OCPP Tag is required"),
});

export const onboardcustomerValidationSchema = Yup.object().shape({
  name: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Name is required"),
  phone: Yup.string()
    .matches(/^[0-9\-+ ]{8,14}$/, "Invalid phone number") // Validating phone number format
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Phone number is required"),
  email: Yup.string()
    .email("Invalid email address") // Validating email format
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Email is required"),
  vehicleVin: Yup.string()
    .matches(/^[A-HJ-NPR-Z0-9]{17}$/, "Invalid Vehicle VIN number") // VIN number format: 17 characters, no I, O, Q
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Vehicle VIN is required"),
});

export const serviceRequestFormValidationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid mail address")
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Email is required"),
  phone: Yup.string().matches(/^[0-9\- ]{8,14}$/, "Invalid phone number"),
  issueType: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Choose the issue type"),
  description: Yup.string()
    .max(100, "Must be 100 characters or less")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Give some details"),
  summary: Yup.string()
    .max(200, "Must be 200 characters or less")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Summary is required"),
});

export const paymentMethodValidationSchema = Yup.object().shape({
  paymentGateway: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Required"),

  public_key: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Required"),
  secret_key: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Required"),
  merchant_id: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis),
  webhook_key: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Required"),

  config_json: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .test("is-json", "Invalid JSON format", (value) => {
      if (!value) return true;
      try {
        JSON.parse(value);
        return true;
      } catch (err) {
        return false;
      }
    }),
});

export const productFormvalidationSchema = Yup.object().shape({
  icon: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Icon is required"),
  title: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Title is required"),
  description: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Description is required"),
  url: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .url("Invalid URL")
    .required("URL is required"),
  access_type: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Access type is required"),
  category: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Category is required"),
});

export const productCategoryValidationSchema = Yup.object().shape({
  category: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Category name is required"),
});

export const filterCategoryValidationSchema = Yup.object().shape({
  name: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Category name is required"),
});

export const filterItemValidationSchema = Yup.object().shape({
  name: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Name is required"),
  item_key: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Key is required"),
  category_id: Yup.mixed().required("Category is required"),
  image_url: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .required("Image is required"),
});

export const yourbulksmsValidationSchema = Yup.object().shape({
  config: Yup.object().shape({
    authkey: Yup.string()
      .transform(sanitizeString)
      .test("sanitized", "Input contained invalid characters", sanitizationTest)
      .test('no-emojis', 'Emojis are not allowed', noEmojis)
      .required('Auth Key is required'),
    sender: Yup.string()
      .transform(sanitizeString)
      .test("sanitized", "Input contained invalid characters", sanitizationTest)
      .test('no-emojis', 'Emojis are not allowed', noEmojis)
      .required('Sender ID is required'),
    route: Yup.string()
      .transform(sanitizeString)
      .test("sanitized", "Input contained invalid characters", sanitizationTest)
      .test('no-emojis', 'Emojis are not allowed', noEmojis)
      .required('Route is required'),
    country: Yup.string()
      .transform(sanitizeString)
      .test("sanitized", "Input contained invalid characters", sanitizationTest)
      .test('no-emojis', 'Emojis are not allowed', noEmojis)
      .required('Country is required'),
    base_url: Yup.string()
      .url('Must be a valid URL')
      .transform(sanitizeString)
      .test("sanitized", "Input contained invalid characters", sanitizationTest)
      .test('no-emojis', 'Emojis are not allowed', noEmojis)
      .required('Base URL is required'),
  })
});

export const smsTemplateValidationSchema = (templateVariable) => Yup.object().shape({
  template_name: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test('no-emojis', 'Emojis are not allowed', noEmojis)
    .required("Template Name is required"),
  dlt_template_id: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test('no-emojis', 'Emojis are not allowed', noEmojis)
    .required("DLT Template ID is required"),
  template_body: Yup.string()
    .required("Template Body is required")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test('no-emojis', 'Emojis are not allowed', noEmojis)
    .test(
      "valid-variables",
      "Template Body contains invalid or empty variable(s)",
      function (value) {
        const matches = [...(value?.matchAll(/{([^{}]+)}/g) || [])];

        // Return true if all variables used in template_body exist in allowedVariables
        return matches.every(([, variable]) =>
          templateVariable.includes(variable)
        );
      }),
});

export const esendexValidationSchema = Yup.object().shape({
  config: Yup.object().shape({
    username: Yup.string()
      .transform(sanitizeString)
      .test("sanitized", "Input contained invalid characters", sanitizationTest)
      .test('no-emojis', 'Emojis are not allowed', noEmojis)
      .required('Username is required'),
    password: Yup.string()
      .transform(sanitizeString)
      .test("sanitized", "Input contained invalid characters", sanitizationTest)
      .test('no-emojis', 'Emojis are not allowed', noEmojis)
      .required('Password is required'),
    accountreference: Yup.string()
      .transform(sanitizeString)
      .test("sanitized", "Input contained invalid characters", sanitizationTest)
      .test('no-emojis', 'Emojis are not allowed', noEmojis)
      .required('Account Reference is required'),
    base_url: Yup.string()
      .url('Must be a valid URL')
      .transform(sanitizeString)
      .test("sanitized", "Input contained invalid characters", sanitizationTest)
      .test('no-emojis', 'Emojis are not allowed', noEmojis)
      .required('Base URL is required'),
  })
});
export const payoutScheduleValidationSchema = Yup.object().shape({
  frequencyType: Yup.string()
    .oneOf(["WEEKLY", "MONTHLY_NTH_WEEKDAY", "MONTHLY_FIXED_DATE"], "Invalid frequency type")
    .required("Frequency type is required"),
  // Multi-day: array of weekdays for WEEKLY
  daysOfWeek: Yup.array()
    .of(Yup.number().min(0).max(6))
    .when("frequencyType", {
      is: "WEEKLY",
      then: Yup.array().of(Yup.number().min(0).max(6)).min(1, "Select at least one day").required("Days of week required"),
      otherwise: Yup.array().nullable(),
    }),
  // Single day for MONTHLY_NTH_WEEKDAY
  dayOfWeek: Yup.number()
    .nullable()
    .when("frequencyType", {
      is: "MONTHLY_NTH_WEEKDAY",
      then: Yup.number().required("Day of week is required").min(0).max(6),
      otherwise: Yup.number().nullable(),
    }),
  nthOccurrence: Yup.number()
    .nullable()
    .when("frequencyType", {
      is: "MONTHLY_NTH_WEEKDAY",
      then: Yup.number().required("Occurrence is required").min(1).max(4),
      otherwise: Yup.number().nullable(),
    }),
  // Multi-date: array of days for MONTHLY_FIXED_DATE
  daysOfMonth: Yup.array()
    .of(Yup.number().min(1).max(28))
    .when("frequencyType", {
      is: "MONTHLY_FIXED_DATE",
      then: Yup.array().of(Yup.number().min(1).max(28)).min(1, "Select at least one date").required("Days of month required"),
      otherwise: Yup.array().nullable(),
    }),
});

export const ocpiWizardValidationSchema = [
  // Step 1
  Yup.object({
    companyId: Yup.string().required("Company is required"),
  }),
  // Step 2
  Yup.object({
    partnerName: Yup.string().required("Partner Name is required"),
    contactEmail: Yup.string()
      .email("Invalid email")
      .required("Contact Email is required"),
    partyId: Yup.string()
      .required("Party ID is required")
      .matches(/^[A-Z]{3}$/, "Party ID must be exactly 3 uppercase letters"),
    countryCode: Yup.string().required("Country Code is required"),
    ocpiRole: Yup.string().required("OCPI Role is required"),
    ocpiVersion: Yup.array().of(Yup.string()).min(1, "Select at least one OCPI Version").required("OCPI Version is required"),
    businessWebsite: Yup.string().required("Business Website is required")
  }),
  // Step 3 (Enhanced Technical Config)
  Yup.object({
    baseUrl: Yup.string().url("Invalid URL").required("Base URL is required"),
    token: Yup.string().required("Authentication Token is required"),
    clientCert: Yup.mixed().nullable(),
    ipWhitelist: Yup.array().of(
      Yup.string()
        .matches(
          /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
          "Invalid IP address"
        )
    ),
    syncMode: Yup.string().required("Sync Mode is required"),
    syncInterval: Yup.number().min(1).required("Sync Interval is required"),
  }),
  // Step 4 (Credential Exchange)
  Yup.object({
    credentialExchangeStatus: Yup.object({
      handshakeStatus: Yup.string().oneOf(['Completed', 'Pending', 'Failed']).required(),
      tokenStatus: Yup.string().oneOf(['Completed', 'Pending', 'Failed']).required(),
      discoveryStatus: Yup.string().oneOf(['Completed', 'Pending', 'Failed']).required(),
      validationStatus: Yup.string().oneOf(['Completed', 'Pending', 'Failed']).required(),
      completed: Yup.boolean().oneOf([true], 'Credential exchange must be completed'),
    }).required('Credential exchange must be completed'),
  }),
  // Step 5 (Partner Verification, can be expanded)
  Yup.object({}),
  // Step 6 (no required fields, just review)
  Yup.object({}),
];

// OCPI Command validation schemas using existing validation patterns
export const getCommandsValidationSchema = (commandType) => {
  let baseSchema = Yup.object({
    type: Yup.string()
      .transform(sanitizeString)
      .test('sanitized', 'Input contained invalid characters', sanitizationTest)
      .test('no-emojis', 'Emojis are not allowed', noEmojis)
      .required('Command type is required')
  });

  switch (commandType) {
    case 'START_SESSION':
      return baseSchema.concat(Yup.object({
        locationId: Yup.string()
          .transform(sanitizeString)
          .test('sanitized', 'Input contained invalid characters', sanitizationTest)
          .test('no-emojis', 'Emojis are not allowed', noEmojis)
          .required('Location ID is required'),
        evseUid: Yup.string()
          .transform(sanitizeString)
          .test('sanitized', 'Input contained invalid characters', sanitizationTest)
          .test('no-emojis', 'Emojis are not allowed', noEmojis)
          .required('EVSE UID is required'),
        connectorId: Yup.string()
          .transform(sanitizeString)
          .test('sanitized', 'Input contained invalid characters', sanitizationTest)
          .test('no-emojis', 'Emojis are not allowed', noEmojis)
          .required('Connector ID is required'),
        authorizationReference: Yup.string()
          .transform(sanitizeString)
          .test('sanitized', 'Input contained invalid characters', sanitizationTest)
          .test('no-emojis', 'Emojis are not allowed', noEmojis)
          .required('Authorization Reference is required'),
        responseUrl: Yup.string()
          .url('Must be a valid URL')
          .transform(sanitizeString)
          .test('sanitized', 'Input contained invalid characters', sanitizationTest)
          .test('no-emojis', 'Emojis are not allowed', noEmojis)
      }));
    
    case 'STOP_SESSION':
      return baseSchema.concat(Yup.object({
        sessionId: Yup.string()
          .transform(sanitizeString)
          .test('sanitized', 'Input contained invalid characters', sanitizationTest)
          .test('no-emojis', 'Emojis are not allowed', noEmojis)
          .required('Session ID is required'),
        responseUrl: Yup.string()
          .url('Must be a valid URL')
          .transform(sanitizeString)
          .test('sanitized', 'Input contained invalid characters', sanitizationTest)
          .test('no-emojis', 'Emojis are not allowed', noEmojis)
      }));
    
    case 'UNLOCK_CONNECTOR':
      return baseSchema.concat(Yup.object({
        locationId: Yup.string()
          .transform(sanitizeString)
          .test('sanitized', 'Input contained invalid characters', sanitizationTest)
          .test('no-emojis', 'Emojis are not allowed', noEmojis)
          .required('Location ID is required'),
        evseUid: Yup.string()
          .transform(sanitizeString)
          .test('sanitized', 'Input contained invalid characters', sanitizationTest)
          .test('no-emojis', 'Emojis are not allowed', noEmojis)
          .required('EVSE UID is required'),
        connectorId: Yup.string()
          .transform(sanitizeString)
          .test('sanitized', 'Input contained invalid characters', sanitizationTest)
          .test('no-emojis', 'Emojis are not allowed', noEmojis)
          .required('Connector ID is required'),
        responseUrl: Yup.string()
          .url('Must be a valid URL')
          .transform(sanitizeString)
          .test('sanitized', 'Input contained invalid characters', sanitizationTest)
          .test('no-emojis', 'Emojis are not allowed', noEmojis)
      }));
    
    case 'RESERVE_NOW':
      return baseSchema.concat(Yup.object({
        locationId: Yup.string()
          .transform(sanitizeString)
          .test('sanitized', 'Input contained invalid characters', sanitizationTest)
          .test('no-emojis', 'Emojis are not allowed', noEmojis)
          .required('Location ID is required'),
        evseUid: Yup.string()
          .transform(sanitizeString)
          .test('sanitized', 'Input contained invalid characters', sanitizationTest)
          .test('no-emojis', 'Emojis are not allowed', noEmojis)
          .required('EVSE UID is required'),
        authorizationReference: Yup.string()
          .transform(sanitizeString)
          .test('sanitized', 'Input contained invalid characters', sanitizationTest)
          .test('no-emojis', 'Emojis are not allowed', noEmojis)
          .required('Authorization Reference is required'),
        reservationId: Yup.string()
          .transform(sanitizeString)
          .test('sanitized', 'Input contained invalid characters', sanitizationTest)
          .test('no-emojis', 'Emojis are not allowed', noEmojis)
          .required('Reservation ID is required'),
        responseUrl: Yup.string()
          .url('Must be a valid URL')
          .transform(sanitizeString)
          .test('sanitized', 'Input contained invalid characters', sanitizationTest)
          .test('no-emojis', 'Emojis are not allowed', noEmojis)
      }));
    
    case 'CANCEL_RESERVATION':
      return baseSchema.concat(Yup.object({
        reservationId: Yup.string()
          .transform(sanitizeString)
          .test('sanitized', 'Input contained invalid characters', sanitizationTest)
          .test('no-emojis', 'Emojis are not allowed', noEmojis)
          .required('Reservation ID is required'),
        responseUrl: Yup.string()
          .url('Must be a valid URL')
          .transform(sanitizeString)
          .test('sanitized', 'Input contained invalid characters', sanitizationTest)
          .test('no-emojis', 'Emojis are not allowed', noEmojis)
      }));
    
    default:
      return baseSchema;
  }
};

export const promoCodeValidationSchema = (teamCurrencySymbol = "₹") => Yup.object().shape({
  code: Yup.string()
    .matches(/^[A-Z0-9]+$/, "Promo code must be uppercase letters/numbers only")
    .max(50, "Must be 50 characters or less")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test('no-emojis', 'Emojis are not allowed', noEmojis)
    .required("Promo code is required"),
  name: Yup.string()
    .max(50, "Must be 50 characters or less")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test('no-emojis', 'Emojis are not allowed', noEmojis)
    .required("Name is required"),
  description: Yup.string()
    .max(255, "Must be 255 characters or less")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test('no-emojis', 'Emojis are not allowed', noEmojis)
    .nullable(),
  discount_type: Yup.string()
    .oneOf(['PERCENTAGE', 'FIXED'], "Must be either PERCENTAGE or FIXED")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test('no-emojis', 'Emojis are not allowed', noEmojis)
    .required("Discount Type is required"),
  discount_type_for: Yup.string()
    .oneOf(['SIGNUP', 'CHARGING'], "Must be either SIGNUP or CHARGING")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test('no-emojis', 'Emojis are not allowed', noEmojis)
    .required("Discount Type For is required"),
  discount_value: Yup.number()
    .typeError("Discount value must be a number")
    .when("discount_type", {
      is: "PERCENTAGE",
      then: (schema) =>
        schema
          .required("Percentage is required")
          .min(1, "Must be at least 1%")
          .max(100, "Cannot exceed 100%"),
      otherwise: (schema) =>
        schema
          .required("Amount is required")
          .min(1, `Must be at least ${teamCurrencySymbol}1`),
    }),
  max_discount_value: Yup.number()
    .typeError("Max discount value must be a number")
    .when("discount_type", {
      is: "PERCENTAGE",
      then: (schema) => schema.min(0, "Must be positive").nullable(),
      otherwise: (schema) => schema.nullable(),
    }),
  min_trx_value: Yup.number()
    .typeError("Min transaction value must be a number")
    .min(0, "Must be positive or zero")
    .test('min-trx-vs-discount', 'Min transaction value validation failed', function(value) {
      const { discount_type, discount_value, max_discount_value } = this.parent;
      
      if (!value || value === 0) return true; // Allow 0 or empty
      
      if (discount_type === 'FIXED' && discount_value) {
        if (value <= discount_value) {
          return this.createError({ 
            message: `Min transaction value (${teamCurrencySymbol}${value}) must be greater than discount value (${teamCurrencySymbol}${discount_value})`
          });
        }
      }
      
      if (discount_type === 'PERCENTAGE' && max_discount_value) {
        if (value <= max_discount_value) {
          return this.createError({ 
            message: `Min transaction value (${teamCurrencySymbol}${value}) must be greater than max discount value (${teamCurrencySymbol}${max_discount_value})`
          });
        }
      }
      
      return true;
    })
    .nullable(),
  valid_from: Yup.date()
    .typeError("Please enter a valid date")
    .required("Valid From is required"),
  valid_to: Yup.date()
    .typeError("Please enter a valid date")
    .required("Valid Until is required")
    .min(Yup.ref("valid_from"), "Must be after start date"),
  usage_limit: Yup.number()
    .typeError("Usage limit must be a number")
    .integer("Usage limit must be a whole number")
    .min(0, "Must be 0 or greater")
    .required("Usage limit is required"),
  // usage_frequency: Yup.number()
  //   .typeError("Usage frequency must be a number")
  //   .integer("Usage frequency must be a whole number")
  //   .min(1, "Must be 1 or greater")
  //   .required("Usage frequency is required"),
  is_active: Yup.boolean()
    .required("Status is required"),
});

export const onboardingValidationSchema = Yup.object().shape({
  firstName: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("First name required"),
  lastName: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Last name required"),
  email: Yup.string()
    .email("Email must be a valid email address")
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Email is required"),
    companyName: Yup.string().when("useCase", {
  is: "business",
  then: (schema) => schema.required("Company name is required"),
  otherwise: (schema) => schema.notRequired(),
}),
chargerCount: Yup.number().when("useCase", {
  is: "business",
  then: (schema) => schema.min(1, "Must be at least 1").integer("Must be a whole number"),
  otherwise: (schema) => schema.notRequired(),
}),

    country: Yup.string().required("Country is required"),

  useCase: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .required("Please select what you need"),
  chargingSetup: Yup.string()
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis)
    .max(500, "Must be 500 characters or less")
    .nullable()
    .notRequired(),
  acceptedTerms: Yup.boolean().oneOf(
    [true],
    "Please accept the terms to continue"
  ),
});
